import { isAnyOf } from '@reduxjs/toolkit';

import { v4 } from 'uuid';
import ooeConstants from '../constants';
import { actions as diagnosticMessagesActions } from '../diagnosticMessages/reducer';
import { DiagnosticMessageType } from '../diagnosticMessages/types';
import { AppStartListening } from '../listenerMiddleware';
import { selectIndexedMenuItems, selectMenuPaperGoods } from '../menu/selectors';
import { getMenus } from '../menu/thunks';
import { setGuestCountToPaperGoodsMax } from '../menu/utils';
import { actions as orderActions } from '../order/reducers';
import { submitOrder } from '../order/thunks';
import { State } from '../reducers';
import { logEvent } from '../services/analytics';
import history from '../util/history';
import { ensureValueIsWholeNumber } from '../util/validate';
import { actions as cartActions } from './reducer';
import { selectAllCartItems, selectPaperGoodsCartItems } from './selectors';

const addCartListeners = (startListening: AppStartListening) => {
  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: (action, listenerApi) => {
      const {
        order: { lineItems },
      } = action.payload;
      const { dispatch } = listenerApi;
      dispatch(cartActions.mapLineItemsToCartItems({ lineItems }));
      // Kick pricing after cart is filled so updateOrder does not race listener order / debounce.
      dispatch(orderActions.updatePricing());
    },
  });

  startListening({
    actionCreator: orderActions.exitEditOrder,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(cartActions.clearCart());
    },
  });

  startListening({
    actionCreator: submitOrder.fulfilled,
    effect: (_, listenerApi) => {
      const { dispatch } = listenerApi;
      dispatch(cartActions.clearCart());
    },
  });

  startListening({
    matcher: isAnyOf(
      cartActions.addToCart,
      cartActions.updateQuantity,
      cartActions.deleteItem,
      cartActions.removeManyItems,
      cartActions.addModifier,
      cartActions.updateModifierQuantity,
      cartActions.updateSpecialInstructions,
      cartActions.updateSideItem,
      cartActions.updateDessertItem,
      cartActions.updatePremiumSideItem,
      cartActions.makePromoFree,
      cartActions.removePromoFree,
    ),
    effect: (action) => {
      const { type } = action;
      logEvent(type);
    },
  });

  startListening({
    actionCreator: getMenus.fulfilled,
    effect: (_, { getState, dispatch }) => {
      const state = getState() as State;
      const menuItems = selectIndexedMenuItems(state);
      const cartItems = selectAllCartItems(state);

      const unavailableItemTags = cartItems
        .map((item) => item.tag)
        .filter((tag) => menuItems[tag] === undefined);

      if (unavailableItemTags.length > 0) {
        dispatch(cartActions.removeNonExistentItems({ tags: unavailableItemTags }));
      }
    },
  });

  startListening({
    actionCreator: cartActions.clearAllPaperGoods,
    effect: (_, { getState, dispatch }) => {
      const state = getState() as State;
      const ids = selectPaperGoodsCartItems(state).map((item) => item.id);

      if (ids.length > 0) {
        dispatch(cartActions.removeManyItems({ ids }));
      }
    },
  });

  startListening({
    actionCreator: cartActions.updateAllPaperGoods,
    effect: (action, { getState, dispatch }) => {
      const state = getState() as State;
      const guestCount = ensureValueIsWholeNumber(setGuestCountToPaperGoodsMax(action.payload.quantity));

      if (guestCount <= 0) {
        return;
      }

      const paperGoodsCategory = selectMenuPaperGoods(state);
      const cartItemsByTag = new Map(selectPaperGoodsCartItems(state).map((item) => [item.tag, item]));

      (paperGoodsCategory?.items ?? []).forEach((item) => {
        const existingCartItem = cartItemsByTag.get(item.tag);
        const id = existingCartItem?.id || v4();

        if (!existingCartItem) {
          dispatch(cartActions.addToCart({ id, itemTag: item.tag, openCartModal: false }));
        }

        dispatch(cartActions.updateQuantity({ id, quantity: guestCount }));
      });
    },
  });

  startListening({
    actionCreator: cartActions.removeNonExistentItems,
    effect: (_, { dispatch }) => {
      dispatch(
        diagnosticMessagesActions.addMessage({
          message: {
            type: DiagnosticMessageType.warning,
            text: ooeConstants.DIAGNOSTIC_MESSAGES.REMOVED_CART_ITEMS,
            dismissible: true,
          },
        }),
      );
    },
  });

  startListening({
    actionCreator: cartActions.addToCart,
    effect: (action) => {
      const { id, openCartModal = true } = action.payload;
      const locationState = { state: { modal: openCartModal, cartItemId: id } };

      // update state if cart modal is already showing
      if (history.location.state && history.location.state.modal) {
        return history.replace(locationState);
      }

      return history.push(locationState);
    },
  });
};

export default addCartListeners;
